import { apiClient } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type {
  CalendarEvent,
  CreateScheduleRequest,
  Schedule,
  ScheduleListItem,
} from "../types";

export async function listSchedules(): Promise<ScheduleListItem[]> {
  const response =
    await apiClient.get<ApiResponse<ScheduleListItem[]>>("/schedules");
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load schedules");
}

export async function getSchedule(id: string): Promise<Schedule> {
  const response = await apiClient.get<ApiResponse<Schedule>>(
    `/schedules/${id}`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load schedule");
}

export async function createSchedule(
  data: CreateScheduleRequest,
): Promise<Schedule> {
  const response = await apiClient.post<ApiResponse<Schedule>>(
    "/schedules",
    data,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to create schedule");
}

export async function deleteSchedule(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<null> | "">(
    `/schedules/${id}`,
  );
  // 204 No Content yields an empty body — treat as success.
  if (response.status === 204) return;
  if (typeof response.data === "object" && response.data !== null) {
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete schedule");
    }
  }
}

export async function getScheduleEvents(
  id: string,
  fromUtc: string,
  toUtc: string,
): Promise<CalendarEvent[]> {
  const response = await apiClient.get<ApiResponse<CalendarEvent[]>>(
    `/schedules/${id}/events`,
    { params: { from: fromUtc, to: toUtc } },
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load events");
}

export async function refreshSchedule(id: string): Promise<Schedule> {
  const response = await apiClient.post<ApiResponse<Schedule>>(
    `/schedules/${id}/refresh`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to refresh schedule");
}

export async function revalidateSchedule(id: string): Promise<Schedule> {
  const response = await apiClient.post<ApiResponse<Schedule>>(
    `/schedules/${id}/revalidate`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to re-run validations");
}
